import React, { useEffect, useState } from 'react';
import BannerImage from '@assets/svg/Roadmaps_text.svg';
import { Button } from '@components/ui';
import styles from './RoadmapsWelcomePage.module.css';
import { useRoadmapRequestEmailMutation } from '@hooks/roadmaps';

const RoadmapsWelcomePage: React.FC = () => {
  const [emailSent, setEmailSent] = useState(false);
  const roadmapRequestMutation = useRoadmapRequestEmailMutation();

  // Check if email was already sent (from localStorage)
  useEffect(() => {
    const wasEmailSent = localStorage.getItem('roadmapEmailSent');
    if (wasEmailSent === 'true') {
      setEmailSent(true);
    }
  }, []);

  const handleStartRoadmap = async () => {
    try {
      await roadmapRequestMutation.mutateAsync();
      setEmailSent(true);
      localStorage.setItem('roadmapEmailSent', 'true');
    } catch (error) {
      console.error('Failed to send roadmap request email:', error);
      // Error toast is already handled in the mutation hook
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="roadmap-welcome-page">
      {/* Hero Section with Banner Image as Background */}
      <div data-testid="hero-section"
        className={`relative overflow-hidden h-96 lg:h-[500px] flex items-center bg-gray-800 ${styles.heroBanner}`}
        style={{ '--banner-image': `url(${BannerImage})` } as React.CSSProperties}
      />

      {/* Content Section - Proper Indentation */}
      <div className="px-6 ml-40 py-16 lg:ml-48 lg:px-18">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-normal text-gray-900 mb-3">
              Build a smarter plan for your tech
            </h2>
            
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed mb-8">
              <p>
                You wouldn&apos;t drive somewhere new without a map — don&apos;t run IT without one.
                Without a clear technology roadmap that takes into account your business
                goals, it&apos;s easy to lose direction or spend money on the wrong things. With
                CloudClevr, you don&apos;t just get recommendations — you get a route. Our
                roadmap gives you tailored insight into where your risks are, what&apos;s holding you
                back, and how to move forward with confidence.
              </p>
              
              <p>
                From infrastructure to security, compliance to services — we&apos;ll show you what
                to prioritise, when to act, and how to measure success.
              </p>
              
              <p className="font-semibold text-gray-900">
                No roadmap yet? Let&apos;s fix that.
              </p>
            </div>

            <h3 className="text-3xl font-normal text-gray-900 mb-3" data-testid="roadmap-importance-heading">
              Why a technology roadmap matters
            </h3>
            
            <div className="space-y-3 mb-8" data-testid="roadmap-benefits-list">
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700 text-lg">Aligns IT priorities to your business goals</span>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700 text-lg">Highlights risks and gaps before they cost you</span>
              </div>
              
              <div className="flex items-start space-x-4" data-testid="roadmap-benefit-item">
                <div className="w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700 text-lg">Replaces guesswork with clear actions</span>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700 text-lg">Helps you plan budgets and timelines with confidence</span>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700 text-lg">Drives better decisions around infrastructure, security, compliance and more</span>
              </div>
            </div>

            <h3 className="text-3xl font-normal text-gray-900 mb-3">
              Don&apos;t just fix IT. Future-proof it.
            </h3>
            
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed mb-8">
              <p>
                Every roadmap starts with a 1:1 review. We&apos;ll assess your current state, map your
                business goals, and create a clear path to smarter, safer, more strategic IT.
              </p>
              
              <p>
                Technology with direction. Strategy with results. <span className="font-semibold text-gray-900">That&apos;s Clevr.</span>
              </p>
            </div>

            <div>
              {emailSent ? (
                <div data-testid="email-sent-message" className="bg-green-100 border border-green-300 rounded-lg p-6 flex items-start space-x-4"> 
                <div className="pt-0.5">
                  <div className="w-6 h-6 rounded-full border-2 border-green-600 flex items-center justify-center flex-shrink-0 pt-0.5 ">
                    <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>  
                  </div>                                 
                  <div className="pt-0 ">
                    <h4 className="text-lg text-gray-900 mb-2" data-testid="email-sent-heading">We&apos;ll be in touch</h4>
                    <p className="text-gray-700" data-testid="email-sent-text">
                      One of our team will be in touch about your roadmap within the next 24 hours.
                    </p>
                  </div>
                </div>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleStartRoadmap}
                  data-testid="start-roadmap-button"
                  disabled={roadmapRequestMutation.isPending}
                  className="bg-green-400 hover:bg-green-450 text-black px-5 py-4 rounded-full text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {roadmapRequestMutation.isPending ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className='pl-2'>Sending request...</span>
                    </>
                  ) : (
                    <span data-testid="start-roadmap-button-text">Start your roadmap</span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { RoadmapsWelcomePage };